import { useState } from 'react';

import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Check, CircleAlert, ImageOff, Trash2 } from 'lucide-react';

import type { ImageGalleryItem } from '@modules/_shared/components/image-gallery/NewImageGallery';
import type { GalleryUploadingImage } from '@modules/_shared/components/image-gallery/useGalleryUploads';
import { useLocale } from '@shared/hooks/locale';

const SKELETON_COUNT = 8;

const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(144px, 1fr))',
  gap: 2,
} as const;

const TILE_SX = {
  position: 'relative',
  width: '100%',
  aspectRatio: '1',
  borderRadius: '12px',
  overflow: 'hidden',
  bgcolor: 'grey.100',
} as const;

const TileImage = ({ url, dimmed }: { url: string; dimmed?: boolean }) => (
  <Box
    component="img"
    src={url}
    alt=""
    sx={{
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
      opacity: dimmed ? 0.5 : 1,
    }}
  />
);

const PhotoTile = ({
  image,
  onSelect,
  onDelete,
}: {
  image: ImageGalleryItem;
  onSelect: (image: ImageGalleryItem) => void;
  onDelete?: (id: string) => void;
}) => {
  const { t } = useLocale();
  const [failed, setFailed] = useState(false);
  const isCurrent = image.is_primary === true;
  const url = image.image.url;

  return (
    <Box
      sx={{
        position: 'relative',
        '&:hover .gallery-tile-delete, &:focus-within .gallery-tile-delete': {
          opacity: 1,
        },
        '@media (hover: none)': {
          '& .gallery-tile-delete': { opacity: 1 },
        },
      }}
    >
      <ButtonBase
        onClick={() => onSelect(image)}
        disabled={failed || !url}
        sx={{
          ...TILE_SX,
          outline: '1px solid',
          outlineColor: 'divider',
          transition: 'outline-color 150ms ease',
          ...(isCurrent && {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          }),
          '&:hover': { outlineColor: 'primary.main' },
          '&.Mui-focusVisible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
        }}
      >
        {failed || !url ? (
          <Stack
            spacing={1}
            sx={{ alignItems: 'center', color: 'text.disabled', px: 1 }}
          >
            <ImageOff size={20} />
            <Typography variant="caption" sx={{ textWrap: 'balance' }}>
              {t('Could not load this photo')}
            </Typography>
          </Stack>
        ) : (
          <Box
            component="img"
            src={url}
            alt=""
            loading="lazy"
            onError={() => setFailed(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        )}
      </ButtonBase>
      {isCurrent && (
        <Chip
          icon={<Check size={14} />}
          label={t('Current')}
          size="small"
          color="primary"
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            height: 24,
            pointerEvents: 'none',
          }}
        />
      )}
      {onDelete && image.canDelete !== false && (
        <IconButton
          className="gallery-tile-delete"
          size="small"
          aria-label={t('Delete image')}
          onClick={() => onDelete(image.id)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 32,
            height: 32,
            borderRadius: '8px',
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            color: 'text.secondary',
            opacity: 0,
            transition: 'opacity 150ms ease, color 150ms ease',
            '&:hover': { bgcolor: 'background.paper', color: 'error.main' },
            '&::after': { content: '""', position: 'absolute', inset: -5 },
          }}
        >
          <Trash2 size={16} />
        </IconButton>
      )}
    </Box>
  );
};

const UploadingTile = ({ upload }: { upload: GalleryUploadingImage }) => {
  const { t } = useLocale();

  return (
    <Box sx={{ ...TILE_SX, outline: '1px solid', outlineColor: 'divider' }}>
      <TileImage url={upload.tempUrl} dimmed />
      <Stack
        spacing={1}
        sx={{
          position: 'absolute',
          inset: 0,
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
          color: upload.state === 'error' ? 'error.main' : 'text.primary',
        }}
      >
        {upload.state === 'error' ? (
          <>
            <CircleAlert size={20} />
            <Typography
              variant="caption"
              title={upload.error.message}
              sx={{
                fontWeight: 500,
                bgcolor: 'background.paper',
                borderRadius: '4px',
                px: 1,
              }}
            >
              {t('Upload error')}
            </Typography>
          </>
        ) : (
          <CircularProgress size={24} thickness={5} />
        )}
      </Stack>
    </Box>
  );
};

export const GalleryPhotoGridSkeleton = () => (
  <Box sx={GRID_SX}>
    {Array.from({ length: SKELETON_COUNT }, (_, index) => (
      <Skeleton
        key={index}
        variant="rounded"
        sx={{
          width: '100%',
          height: 'auto',
          aspectRatio: '1',
          borderRadius: '12px',
        }}
      />
    ))}
  </Box>
);

export const GalleryPhotoGrid = ({
  images,
  uploadingImages,
  onSelectImage,
  onDeleteImage,
}: {
  images: ImageGalleryItem[];
  uploadingImages: GalleryUploadingImage[];
  onSelectImage: (image: ImageGalleryItem) => void;
  onDeleteImage?: (id: string) => void;
}) => {
  const { t } = useLocale();

  if (images.length === 0 && uploadingImages.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}
      >
        {t('No photos to show.')}
      </Typography>
    );
  }

  return (
    <Box sx={GRID_SX}>
      {uploadingImages.map(upload => (
        <UploadingTile key={upload.id} upload={upload} />
      ))}
      {images.map(image => (
        <PhotoTile
          key={image.id}
          image={image}
          onSelect={onSelectImage}
          onDelete={onDeleteImage}
        />
      ))}
    </Box>
  );
};
